import { Box, Link, ListItem, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useShadowPulseAnimation } from "../../hooks/use-shadow-pulse-animation";
import { Icon } from "@iconify/react";
import { useBoolean } from "../../hooks/use-boolean";

//-------------------------------------------------------------

const Iconify = ({ icon, width = 20, sx, ...other }) => (
  <Box
    className="component-iconify"
    icon={icon}
    component={Icon}
    data-inline="false"
    sx={{ width, height: width, ...sx }}
    {...other}
  />
);

const CONTACTS = [
  {
    name: "Messenger",
    icon: "/assets/icons/ic_messenger_bold.svg",
    headline: "Messenger",
    path: "https://m.me/ct.blueskyit",
  },
  {
    name: "Zalo",
    icon: "/assets/icons/ic_zalo_bold.svg",
    headline: "Nhắn tin zalo",
    path: "#",
  },
  {
    name: "Phone",
    icon: "/assets/icons/ic_call_bold.svg",
    headline: "0968.596.859 ",
    path: "",
  },
  {
    name: "Gmail",
    icon: "/assets/icons/ic_sms_bold.svg",
    headline: "lienhe@blueskyit.vn",
    path: "mailto:lienhe@blueskyit.vn",
  },
];

//-------------------------------------------------------------

export default function ConactsWidget() {
  const showIconDefault = useBoolean(true);
  const openPopupConatacts = useBoolean();
  const showContactIcons = useBoolean();
  const showIconClose = useBoolean();

  const [positionTranslate, setPositonTranslate] = useState(0);

  const animationStyle = useShadowPulseAnimation(
    !openPopupConatacts.value,
    "primary"
  );

  const handleCallClick = () => {
    window.location.href = "tel:+0845 830 236";
  };

  useEffect(() => {
    if (
      !openPopupConatacts.value &&
      showIconDefault.value &&
      !showContactIcons.value
    ) {
      const timer = setTimeout(() => {
        showContactIcons.onTrue();
        showIconDefault.onFalse();
      }, 2500);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [showContactIcons, showIconDefault, openPopupConatacts]);

  useEffect(() => {
    const positonMax = (CONTACTS.length - 1) * 60;
    if (
      showContactIcons.value &&
      !showIconDefault.value &&
      !openPopupConatacts.value
    ) {
      if (positionTranslate < -positonMax) {
        showIconDefault.onTrue();
        showContactIcons.onFalse();
        setPositonTranslate(0);
        return () => {};
      }
      if (positionTranslate >= -positonMax) {
        const interval = setInterval(() => {
          setPositonTranslate((prev) => prev - 60);
        }, 1500);
        return () => clearInterval(interval);
      }
    }
    return () => {};
  }, [
    showContactIcons,
    openPopupConatacts,
    showIconDefault,
    positionTranslate,
  ]);

  const toggleContactWidget = () => {
    openPopupConatacts.onToggle();

    if (openPopupConatacts.value) {
      showIconDefault.onTrue();
      showIconClose.onFalse();
      showContactIcons.onFalse();
    } else {
      showIconDefault.onFalse();
      showIconClose.onTrue();
      showContactIcons.onFalse();
    }
    setPositonTranslate(0);
  };

  const openInNewTab = (link) => {
    if (link) {
      const newWindow = window.open(link, "_blank", "noopener,noreferrer");
      if (newWindow) newWindow.opener = null;
    }
  };

  const ButtonFullWidth = ({ icon }) => (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "50%",
        flex: "1",
      }}
    >
      <Iconify icon={icon} sx={{ color: "#fff", width: 30, height: 30 }} />
    </Box>
  );

  const ContactItem = ({ contact, onClick }) => (
    <ListItem
      sx={{
        minHeight: 54,
        cursor: "pointer",
        py: 0,
        px: 1,
      }}
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" columnGap={1}>
        <Box
          key={contact.headline}
          width={40}
          height={40}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            ml: 0.5,
          }}
        >
          <Box
            component="img"
            src={contact.icon}
            sx={{ width: 26, height: 26 }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            flex: "1",
            "&:hover": {
              color: "#1C9BDB",
            },
          }}
        >
          {contact.headline}
        </Typography>
      </Stack>
    </ListItem>
  );

  const renderPopupContact = (
    <Stack
      sx={{
        position: "absolute",
        py: 1,
        right: 0,
        bottom: 75,
        width: 300,
        backgroundColor: "#fff",
        borderRadius: 2,
        zIndex: 100,
        "&::before": {
          content: '""',
          width: 0,
          height: 0,
          borderLeft: "14px solid transparent",
          borderRight: "14px solid transparent",
          borderTop: "20px solid #fff",
          position: "absolute",
          bottom: -12,
          right: 16,
        },
        boxShadow:
          "rgba(145, 158, 171, 0.24) 0px 0px 2px 0px, rgba(145, 158, 171, 0.24) -20px 20px 40px -4px",
      }}
    >
      <Stack>
        {CONTACTS.map((contact) =>
          contact.path ? (
            <Link
              href={contact.path}
              onClick={(e) => {
                e.preventDefault();
              }}
              sx={{
                textDecoration: "none",
                color: "#1E293B",
                "&:hover": {
                  textDecoration: "none",
                  color: "#1C9BDB",
                },
              }}
              key={`contact-item-${contact.headline}`}
            >
              <ContactItem
                onClick={() => {
                  openInNewTab(contact.path);
                  openPopupConatacts.onFalse();
                  showIconDefault.onTrue();
                  showIconClose.onFalse();
                }}
                contact={contact}
              />
            </Link>
          ) : (
            <ContactItem
              key={`contact-item-${contact.headline}`}
              contact={contact}
              onClick={() => {
                openInNewTab(contact.path);
                if (contact.name === "Phone") {
                  handleCallClick();
                  openPopupConatacts.onFalse();
                  showIconDefault.onTrue();
                  showIconClose.onFalse();
                }
              }}
            />
          )
        )}
      </Stack>
    </Stack>
  );

  const renderContactIconList = (
    <Stack
      direction="row"
      sx={{
        position: "absolute",
        transform: `translateX(${positionTranslate}px)`,
        transition: "transform 0.5s ease",
      }}
    >
      {CONTACTS.map((contact) => (
        <Box
          key={contact.headline}
          width={60}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box component="img" src={contact.icon} sx={{ width: 26 }} />
        </Box>
      ))}
    </Stack>
  );

  return (
    <>
      <Box sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 9999 }}>
        <Box
          onClick={toggleContactWidget}
          sx={{
            width: 60,
            height: 60,
            position: "absolute",
            borderRadius: "50%",
            bottom: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(180deg, #27C6FF 0%, #1375B7 100%)",
            cursor: "pointer",
            overflow: "hidden",
            ...animationStyle,
          }}
        >
          {showIconDefault.value && (
            <ButtonFullWidth icon="uil:facebook-messenger" />
          )}

          {showIconClose.value && (
            <ButtonFullWidth icon="material-symbols-light:close" />
          )}
          {showContactIcons.value && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                border: "8px solid #1C9BDB",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fff",
              }}
            />
          )}

          {showContactIcons.value && renderContactIconList}
        </Box>

        {openPopupConatacts.value && renderPopupContact}
      </Box>
    </>
  );
}
